import { useCallback } from 'react';

import { isADObject } from '@/sections/asset/utils';
import { useGlobalState } from '@/state/global.state';
import type { Asset, Risk, Seed, Technology } from '@/types';

/**
 * Active Directory key prefixes for entity detection
 */
const AD_KEY_PREFIXES = [
  '#aduser#',
  '#adgroup#',
  '#adcomputer#',
  '#addomain#',
  '#adcontainer#',
  '#adcerttemplate#',
  '#adaiaca#',
  '#adobject#',
  '#adgpo#',
  '#adou#',
  '#adlocalgroup#',
  '#adlocaluser#',
  '#adrootca#',
  '#adenterpriseca#',
  '#adntauthstore#',
  '#adissuancepolicy#',
] as const;

/**
 * Check if a key starts with any AD prefix
 */
function isADKeyPrefix(key: string): boolean {
  return AD_KEY_PREFIXES.some(prefix => key.startsWith(prefix));
}

/**
 * Entity type for drawer opening
 */
export type DrawerEntityType =
  | 'asset'
  | 'vulnerability'
  | 'technology'
  | 'seed';

/**
 * Union type for entities that can be opened in a drawer
 */
export type DrawerEntity = Asset | Risk | Technology | Seed;

/**
 * Return type for the useOpenEntityDrawer hook
 */
export interface UseOpenEntityDrawerReturn {
  /**
   * Open a drawer for a given entity object
   * @param entity - The entity object (Asset, Risk, Technology, or Seed)
   * @param entityType - Optional entity type override. If not provided, will be auto-detected from key prefix.
   */
  openDrawerForEntity: (
    entity: DrawerEntity,
    entityType?: DrawerEntityType
  ) => void;

  /**
   * Open a drawer using only the entity key
   * Creates a minimal entity object and opens the appropriate drawer.
   * @param key - The entity key (e.g., '#asset#example.com', '#risk#...', etc.)
   * @param entityType - Optional entity type override. If not provided, will be auto-detected from key prefix.
   */
  openDrawerByKey: (key: string, entityType?: DrawerEntityType) => void;
}

/**
 * Detect entity type from key prefix
 *
 * @param key - The entity key string
 * @returns The detected entity type or null if not recognized
 */
function detectEntityTypeFromKey(key: string): DrawerEntityType | null {
  if (!key) return null;

  // Asset key prefixes (including AD objects)
  if (
    key.startsWith('#asset#') ||
    isADKeyPrefix(key) ||
    key.startsWith('#repository#') ||
    key.startsWith('#integration#') ||
    key.startsWith('#webapplication#') ||
    key.startsWith('#awsresource#') ||
    key.startsWith('#azureresource#') ||
    key.startsWith('#gcpresource#')
  ) {
    return 'asset';
  }

  if (key.startsWith('#risk#')) {
    return 'vulnerability';
  }

  if (key.startsWith('#technology#')) {
    return 'technology';
  }

  if (key.startsWith('#seed#')) {
    return 'seed';
  }

  return null;
}

/**
 * Check if an entity is an Active Directory object
 *
 * @param entity - The entity to check
 * @returns true if the entity is an AD object
 */
function isActiveDirectoryEntity(entity: DrawerEntity): boolean {
  // Use the isADObject utility from asset utils
  if (isADObject(entity as Asset)) {
    return true;
  }

  // Also check key-based detection for partial entities
  const key = entity.key;
  if (typeof key === 'string') {
    return isADKeyPrefix(key);
  }

  return false;
}

/**
 * Hook to centralize drawer opening logic for entities
 *
 * Provides a consistent pattern for opening entity drawers across the application.
 * Used in Attack Paths nodes, Node Graph, Query Builder, and other contexts.
 *
 * @example
 * ```tsx
 * const { openDrawerForEntity, openDrawerByKey } = useOpenEntityDrawer();
 *
 * // Open drawer with full entity object
 * openDrawerForEntity(assetEntity);
 *
 * // Open drawer with just the key (drawer fetches data internally)
 * openDrawerByKey('#asset#example.com', 'asset');
 *
 * // Auto-detect entity type from key prefix
 * openDrawerByKey('#risk#some-key');
 * ```
 */
export function useOpenEntityDrawer(): UseOpenEntityDrawerReturn {
  const { drawerState } = useGlobalState();

  /**
   * Open a drawer for a given entity object
   */
  const openDrawerForEntity = useCallback(
    (entity: DrawerEntity, entityType?: DrawerEntityType) => {
      if (!entity?.key) return;

      const type = entityType ?? detectEntityTypeFromKey(entity.key);
      if (!type) return;

      switch (type) {
        case 'asset': {
          const isAD = isActiveDirectoryEntity(entity);
          drawerState.handleOpenAssetDrawer(entity as Asset, isAD);
          break;
        }
        case 'vulnerability':
          drawerState.handleOpenVulnerabilityDrawer(entity as Risk);
          break;
        case 'technology':
          drawerState.handleOpenTechnologyDrawer(entity as Technology);
          break;
        case 'seed':
          drawerState.handleOpenSeedDrawer(entity as Seed);
          break;
      }
    },
    [drawerState]
  );

  /**
   * Open a drawer using only the entity key
   * Creates a minimal entity object with just the key - the drawer
   * fetches full data internally via its own data hook.
   */
  const openDrawerByKey = useCallback(
    (key: string, entityType?: DrawerEntityType) => {
      if (!key) return;

      const type = entityType ?? detectEntityTypeFromKey(key);
      if (!type) return;

      // Create minimal entity object with just the key
      // The drawer components fetch full data internally
      const minimalEntity = { key } as DrawerEntity;
      openDrawerForEntity(minimalEntity, type);
    },
    [openDrawerForEntity]
  );

  return { openDrawerForEntity, openDrawerByKey };
}
